import RichText from '@components/elements/rich-text/rich-text';
import { getAppLayoutWithCart } from '@components/layout/app-layout/app-layout';
import LayoutSafeArea from '@components/layout/layout-safe-area/layout-safe-area';
import PageTitleBar from '@components/layout/page-title-bar/page-title-bar';
import {
  v2_asCmsPageFallback,
  v2_FallbackProvider as FallbackProvider,
  v2_fetchCmsPage,
  v2_useCmsPage,
} from '@crocodille/common';
import { assert, assertSome, isSome } from '@crocodille/util';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { cmsPageRoute, CmsPageRouteParams, localized } from '@routing';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { GetStaticPaths, GetStaticProps } from 'next';

const CmsPagePage: AppPage<
  CmsPageRouteParams & {
    readonly fallback: Record<string, unknown>;
    readonly headless?: boolean;
  }
> = ({ slug }) => {
  const { data: cmsPage } = v2_useCmsPage(slug);

  //
  // Page must be prefetched with fallback.
  //
  assert(isSome(cmsPage));

  return (
    <>
      <PageMeta
        pageTitle={cmsPage.title}
        description={cmsPage.metaDescription}
      />
      <LayoutSafeArea>
        <PageTitleBar title={cmsPage.name} />
        <RichText html={cmsPage.content} />
      </LayoutSafeArea>
    </>
  );
};

CmsPagePage.getLayout = (page, { fallback, headless }) => {
  return (
    <FallbackProvider fallback={fallback}>
      {headless ? page : getAppLayoutWithCart(page)}
    </FallbackProvider>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<CmsPageRouteParams> = async (
  context,
) => {
  const slug = assertSome(context.params?.slug);

  assert(typeof slug === 'string');

  const app = getApp(context);

  const cmsPage = await v2_fetchCmsPage(app, slug);

  if (slug === 'franchise') {
    return {
      redirect: {
        destination: 'https://360pizza-franchise.com/',
        permanent: false,
      },
    };
  }

  if (!cmsPage) {
    return {
      notFound: true,
    };
  }

  if (cmsPage.slug !== slug) {
    return {
      redirect: {
        destination: localized(
          app.locale,
          cmsPageRoute({ slug: cmsPage.slug }),
        ),
        permanent: true,
      },
    };
  }

  const translationProps = await getServerSideTranslationProps(app, ['common']);

  return {
    revalidate: 10 * 60, // 10 minutes
    props: {
      slug,
      fallback: {
        ...v2_asCmsPageFallback(app, slug, cmsPage),
      },
      ...translationProps,
    },
  };
};

export default CmsPagePage;
